import { type RetirementCalculatorOutputs } from '@fruitful/financial-calculators';
import { formatDollars, roundToTwoDecimals } from '@fruitful/fruitils';
import React from 'react';
import {
    BodyLargeEmphasized,
    BodyLargeRegular,
    H5Emphasized,
    Icon,
    TargetIcon,
    XStack,
    YStack,
} from 'smoothie';

import { PageContentContainer } from '../../common/components/GroveContainers';

interface RequiredRetirementContributionsProps {
    outputs: RetirementCalculatorOutputs;
}

export const RequiredRetirementContributions = ({
    outputs,
}: RequiredRetirementContributionsProps) => {
    return (
        <PageContentContainer>
            <XStack gap={24} width="100%" alignItems="flex-start">
                {/* Results Summary Section */}
                <YStack gap={8}>
                    <XStack gap={8} alignItems="center">
                        <Icon icon={TargetIcon} size={20} color="$red900" />
                        <H5Emphasized>
                            Required Retirement Contribution Amount
                        </H5Emphasized>
                    </XStack>
                    <BodyLargeRegular>
                        Annual Retirement Contribution:{' '}
                        <BodyLargeEmphasized>
                            {formatDollars(
                                outputs.requiredAnnualRetirementSavings,
                            )}
                        </BodyLargeEmphasized>
                    </BodyLargeRegular>
                    <BodyLargeRegular>
                        Monthly Retirement Contribution:{' '}
                        <BodyLargeEmphasized>
                            {formatDollars(
                                roundToTwoDecimals(
                                    outputs.requiredAnnualRetirementSavings /
                                        12,
                                ),
                            )}
                        </BodyLargeEmphasized>
                    </BodyLargeRegular>
                </YStack>

                {/* Required Annual Contributions Section */}
                <YStack gap={16} flex={1}>
                    <XStack gap={8} alignItems="center">
                        <Icon icon={TargetIcon} size={20} color="$red900" />
                        <H5Emphasized>
                            Required Annual Contributions by Account
                        </H5Emphasized>
                    </XStack>

                    {/* Accounts Grid - Wraps to multiple rows */}
                    <YStack gap={12}>
                        <XStack gap={12} flexWrap="wrap">
                            {outputs.requiredAnnualContributions.map(
                                (contribution, index) => (
                                    <YStack
                                        key={index}
                                        gap={8}
                                        padding={16}
                                        backgroundColor="$gray100"
                                        borderRadius={8}
                                        borderWidth={1}
                                        borderColor="$gray200"
                                        minWidth={280}
                                        maxWidth={320}
                                    >
                                        <BodyLargeRegular>
                                            Account ID:{' '}
                                            <BodyLargeEmphasized>
                                                {contribution.account.id}
                                            </BodyLargeEmphasized>
                                        </BodyLargeRegular>

                                        <BodyLargeRegular>
                                            Employee Contribution:{' '}
                                            <BodyLargeEmphasized>
                                                {formatDollars(
                                                    contribution.employeeContribution,
                                                )}
                                            </BodyLargeEmphasized>
                                        </BodyLargeRegular>

                                        <BodyLargeRegular>
                                            Employer Contribution:{' '}
                                            <BodyLargeEmphasized>
                                                {formatDollars(
                                                    contribution.employerContribution ??
                                                        0,
                                                )}
                                            </BodyLargeEmphasized>
                                        </BodyLargeRegular>

                                        <BodyLargeRegular>
                                            Total Contribution:{' '}
                                            <BodyLargeEmphasized>
                                                {formatDollars(
                                                    contribution.totalContribution,
                                                )}
                                            </BodyLargeEmphasized>
                                        </BodyLargeRegular>
                                    </YStack>
                                ),
                            )}
                        </XStack>
                    </YStack>
                </YStack>
            </XStack>
        </PageContentContainer>
    );
};
